import { ServicePageTemplate, CapabilitiesGrid, ValuePropsGrid, WhyChooseUsSection, FAQSection } from "@/components/templates";
import { createServiceSchema, createFAQSchema } from "@/utils/structuredData";
import {
  Rocket,
  Clock,
  DollarSign,
  Zap,
  Code2,
  Smartphone,
  Cloud,
  Brain,
  Users,
  Shield,
  Target,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Building2,
  ShoppingCart,
  Stethoscope,
  GraduationCap,
  Briefcase,
  BarChart3,
  Globe,
  Layers,
  Star,
  Quote,
  Monitor,
  Server,
  Boxes,
  Plug,
  TabletSmartphone,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { DoubleDiamondProcess } from "@/components/DoubleDiamondProcess";
import { HelpCircle } from "lucide-react";

const mvpFaqItems = [
  {
    question: "Can you build both web and mobile apps for my startup?",
    answer: "Absolutely! We build complete cross-platform solutions — web apps, iOS apps, Android apps, and even desktop apps. Using frameworks like React, Flutter, and React Native, we can deliver apps for all platforms from a single, efficient development effort. Most startups get web + mobile apps delivered together in 3-4 weeks.",
  },
  {
    question: "Do you build custom backends and APIs?",
    answer: "Yes, custom backend development is core to what we do. Whether you need RESTful APIs, GraphQL endpoints, real-time WebSocket connections, or complex authentication systems — we design and build backends tailored to your product requirements. We also integrate with third-party services like Stripe, Twilio, payment gateways, and AI APIs.",
  },
  {
    question: "What about microservices for more complex products?",
    answer: "For advanced software with high scalability needs, we architect microservices-based systems. This approach allows different parts of your application to scale independently, deploy separately, and evolve without affecting other services. Perfect for startups planning to scale to millions of users or building enterprise-grade platforms.",
  },
  {
    question: "How fast can you build my MVP?",
    answer: "Most MVPs are delivered in 2-3 weeks. Full-stack products with web app, mobile apps, custom backend, and API integrations typically take 4-6 weeks. Our streamlined process and experienced team allow us to move fast without cutting corners.",
  },
  {
    question: "How much does full-stack MVP development cost?",
    answer: "Our MVPs typically cost 60-70% less than traditional agencies or hiring in-house teams. Because we handle everything (frontend, backend, mobile, infrastructure), you avoid the overhead of coordinating multiple vendors. We provide transparent, fixed-price quotes after our discovery session.",
  },
  {
    question: "Is the code production-ready or just a prototype?",
    answer: "Every product we build is production-ready from day one. Clean code, proper documentation, scalable architecture, CI/CD pipelines, and security best practices. You can go directly to market and scale to millions of users without rebuilding. Our code regularly passes technical due diligence during funding rounds.",
  },
  {
    question: "Can you integrate with existing systems and APIs?",
    answer: "Yes, API integration is one of our specialties. We integrate with payment gateways (Stripe, Razorpay), communication platforms (Twilio, SendGrid), CRM systems (Salesforce, HubSpot), AI services (OpenAI, AWS AI), and enterprise systems. If it has an API, we can connect to it.",
  },
  {
    question: "Do you provide post-launch support?",
    answer: "Yes! We offer 30 days of free bug fixes after launch. We also provide ongoing development partnerships for startups that want to continue building with us as they grow, add features, and raise funding.",
  },
];

// Generate structured data for SEO
const serviceSchema = createServiceSchema({
  name: "MVP Development Services",
  description:
    "Launch your startup MVP in 2-3 weeks at a fraction of the cost. Production-ready, scalable software built by experienced engineers. Cross-platform mobile apps, web applications, custom APIs, and microservices.",
  url: "https://interestech.com/services/mvp-development",
  serviceType: "MVP Development",
  areaServed: "Worldwide",
});

// FAQ Schema for rich snippets
const faqSchema = createFAQSchema(mvpFaqItems);

const caseStudies = [
  {
    title: "AI-Powered Learning Platform",
    industry: "EdTech",
    timeline: "3 Weeks",
    description:
      "Built a complete learning management system with AI tutoring capabilities, video streaming, and progress tracking for a seed-stage startup.",
    results: ["50K+ users in 6 months", "Secured $2M seed funding", "4.8★ app store rating"],
    technologies: ["React", "Node.js", "OpenAI", "AWS"],
    icon: GraduationCap,
    gradient: "from-blue-500 to-cyan-500",
  },
  {
    title: "Smart Retail POS System",
    industry: "Retail Tech",
    timeline: "2 Weeks",
    description:
      "Developed a cloud-based point-of-sale system with inventory management, analytics dashboard, and multi-location support.",
    results: ["100+ retail stores onboarded", "40% reduction in checkout time", "Series A funded"],
    technologies: ["Flutter", "Firebase", "Stripe", "GCP"],
    icon: ShoppingCart,
    gradient: "from-orange-500 to-red-500",
  },
  {
    title: "Healthcare Appointment Platform",
    industry: "HealthTech",
    timeline: "3 Weeks",
    description:
      "Created a HIPAA-compliant telemedicine platform with video consultations, prescription management, and EHR integration.",
    results: ["10K+ consultations monthly", "Acquired by hospital chain", "99.9% uptime"],
    technologies: ["React Native", "Python", "Twilio", "Azure"],
    icon: Stethoscope,
    gradient: "from-green-500 to-emerald-500",
  },
  {
    title: "B2B SaaS Analytics Tool",
    industry: "Enterprise",
    timeline: "2.5 Weeks",
    description:
      "Built a real-time business intelligence dashboard with custom reporting, data visualization, and team collaboration features.",
    results: ["200+ enterprise clients", "₹50 Lac ARR in year 1", "Featured on Product Hunt"],
    technologies: ["Vue.js", "PostgreSQL", "D3.js", "Docker"],
    icon: BarChart3,
    gradient: "from-purple-500 to-pink-500",
  },
];

const whyMVP = [
  {
    title: "Validate Before You Scale",
    description:
      "Test your assumptions with real users before investing in full development. Reduce risk and pivot quickly based on actual feedback.",
    icon: Target,
  },
  {
    title: "Attract Investors",
    description:
      "A working prototype speaks louder than a pitch deck. Show investors a real product they can interact with and see the potential.",
    icon: TrendingUp,
  },
  {
    title: "First Mover Advantage",
    description:
      "In fast-moving markets, speed matters. Launch before competitors and establish your brand while iterating based on user data.",
    icon: Zap,
  },
  {
    title: "Cost-Effective Learning",
    description:
      "Discover what features truly matter to your users without building everything upfront. Invest in what works.",
    icon: DollarSign,
  },
];

// What We Build - Full-stack capabilities
const whatWeBuild = [
  {
    title: "Cross-Platform Mobile Apps",
    description:
      "One codebase, every device. Build iOS and Android apps simultaneously with Flutter or React Native — native performance, half the development time.",
    icon: TabletSmartphone,
    examples: ["iOS & Android Apps", "Tablet-optimized UIs", "Offline-first Architecture", "Push Notifications"],
    gradient: "from-pink-500 to-rose-500",
  },
  {
    title: "Web Applications",
    description:
      "From simple landing pages to complex SaaS platforms. Responsive, fast, SEO-optimized web apps that work flawlessly on every browser and device.",
    icon: Globe,
    examples: ["Progressive Web Apps (PWA)", "Single Page Applications", "Admin Dashboards", "Customer Portals"],
    gradient: "from-blue-500 to-cyan-500",
  },
  {
    title: "Desktop Applications",
    description:
      "Cross-platform desktop apps using Electron or Tauri. Deploy on Windows, macOS, and Linux with a single codebase — perfect for enterprise tools.",
    icon: Monitor,
    examples: ["Windows, macOS & Linux", "Native System Integration", "Offline Capabilities", "Auto-update Systems"],
    gradient: "from-purple-500 to-violet-500",
  },
  {
    title: "Custom Backend Development",
    description:
      "Secure, scalable backend systems tailored to your needs. From simple REST APIs to complex event-driven architectures — built for growth.",
    icon: Server,
    examples: ["RESTful & GraphQL APIs", "Real-time WebSockets", "Authentication Systems", "Payment Integration"],
    gradient: "from-emerald-500 to-green-500",
  },
  {
    title: "API Development & Integration",
    description:
      "Connect your product to any service. We build robust APIs and seamlessly integrate with third-party platforms, payment gateways, and enterprise systems.",
    icon: Plug,
    examples: ["Stripe & Payment APIs", "CRM Integrations", "AI/ML API Integration", "Custom Webhooks"],
    gradient: "from-orange-500 to-amber-500",
  },
  {
    title: "Microservices Architecture",
    description:
      "For complex, high-scale applications. We design microservices that can scale independently, deploy separately, and evolve with your business needs.",
    icon: Boxes,
    examples: ["Service Mesh Design", "Container Orchestration", "Event-Driven Systems", "Domain-Driven Design"],
    gradient: "from-indigo-500 to-blue-500",
  },
];

const founderTestimonials = [
  {
    name: "Arjun Mehta",
    role: "Founder & CEO",
    company: "AI Edtech",
    quote:
      "We went from a napkin sketch to a fully functional AI tutoring platform in just 18 days. The quality of code was exceptional — our Series A investors' technical team was impressed during due diligence.",
    result: "Raised ₹3.7 Cr Seed Round",
    rating: 5,
    industry: "EdTech",
  },
  {
    name: "Priya Sharma",
    role: "Co-Founder",
    company: "Health Startup",
    quote:
      "As a healthcare startup, we needed HIPAA compliance from day one. Interesting Technologies delivered a telemedicine platform that our patients love and meets all regulatory requirements.",
    result: "5K+ Monthly Consultations",
    rating: 5,
    industry: "HealthTech",
  },
  {
    name: "Rahul Krishnan",
    role: "Technical Founder",
    company: "SaaS Startup",
    quote:
      "I've built products at FAANG companies, and I was skeptical about outsourcing our MVP. The code quality, architecture decisions, and attention to scalability completely changed my mind.",
    result: "Acquired within 18 Months",
    rating: 5,
    industry: "Retail Tech",
  },
  {
    name: "Sneha Patel",
    role: "Founder",
    company: "Finance Startup",
    quote:
      "They understood our fintech compliance needs immediately. The MVP they built helped us onboard our first 50 enterprise clients and prove product-market fit before raising our seed round.",
    result: "₹57 Lac ARR in Year 1",
    rating: 5,
    industry: "FinTech",
  },
];

const MVPDevelopment = () => {
  const navigate = useNavigate();
  return (
    <ServicePageTemplate
      seo={{
        title: "MVP Development — Launch Your Tech Stack in Weeks",
        description:
          "Build web, mobile and desktop MVPs in 2-3 weeks. Production-ready, investor-grade software with APIs and scalable backends, at a fraction of agency cost.",
        keywords:
          "MVP development, cross-platform mobile app development, web application development, desktop app development, API development, custom backend development, microservices architecture, startup MVP, React Native development, Flutter development, full-stack development, SaaS MVP, startup tech partner",
        canonical: "https://interestech.com/services/mvp-development",
        structuredData: [serviceSchema, faqSchema],
        ogImage: "https://interestech.com/itpl-og.webp",
        ogTitle: "Build Your Complete Tech Stack in 2-3 Weeks | Interesting Technologies",
        ogDescription:
          "Web apps, mobile apps, desktop apps, APIs & backends — production-ready in weeks. 70% less than traditional agencies. Launch fast, scale infinitely.",
      }}
      hero={{
        badge: { icon: Rocket, text: "From Idea to Launch in Weeks" },
        title: "Build Your Complete",
        titleHighlight: "Tech Stack in 2-3 Weeks",
        description:
          "Web apps. Mobile apps. Desktop apps. Custom APIs. Scalable backends. Microservices. We build your entire product — not just a prototype. Production-ready, investor-grade software at a fraction of agency costs. Launch fast, scale infinitely.",
        primaryCTA: { text: "Start Your MVP", onClick: () => navigate('/contact') },
        secondaryCTA: { text: "See What We Build", onClick: () => navigate('/our-work') },
        gradient: "from-slate-900/90 via-emerald-900/80 to-slate-900/90",
        accentColor: "green",
        orbitIcons: [Rocket, Code2, TabletSmartphone, Cloud, Server, Boxes],
        backgroundImage: "/lovable-uploads/MVP_Page.webp",
      }}
      stats={{
        items: [
          { label: "Average Delivery Time", value: 3, suffix: " Weeks", icon: Clock },
          { label: "Cost Savings vs Agencies", value: 70, suffix: "%", icon: DollarSign },
          { label: "Startups Launched", value: 20, suffix: "+", icon: Rocket },
          { label: "Client Satisfaction", value: 98, suffix: "%", icon: Users },
        ],
        gradient: "from-emerald-500 to-green-500",
      }}
      features={{
        title: "Everything Included in Your MVP",
        subtitle:
          "We don't just build features — we build complete products. Every MVP ships with everything you need to launch, scale, and win.",
        badge: { icon: CheckCircle, text: "Full-Stack Delivery", color: "green" },
        items: [
          {
            text: "Production-Ready Codebase",
            description:
              "Clean, documented, maintainable code that passes technical due diligence. Investor-ready from day one — not throwaway prototypes.",
            icon: Code2,
            gradient: "from-emerald-500 to-green-500",
          },
          {
            text: "Cross-Platform Mobile Apps",
            description:
              "iOS and Android apps from a single codebase using Flutter or React Native. Native performance, offline support, push notifications.",
            icon: TabletSmartphone,
            gradient: "from-pink-500 to-rose-500",
          },
          {
            text: "Custom Backend & APIs",
            description:
              "Scalable REST/GraphQL APIs, authentication, payment integration, and real-time features. Your backend, built your way.",
            icon: Server,
            gradient: "from-blue-500 to-cyan-500",
          },
          {
            text: "Cloud Infrastructure",
            description:
              "Auto-scaling on AWS, GCP, or Azure. CI/CD pipelines, monitoring, and 99.9% uptime. Go global without infrastructure headaches.",
            icon: Cloud,
            gradient: "from-purple-500 to-pink-500",
          },
          {
            text: "Microservices-Ready Architecture",
            description:
              "Start monolithic, grow into microservices. We design for scale from day one — no rebuilding when you hit 10 million users.",
            icon: Boxes,
            gradient: "from-indigo-500 to-blue-500",
          },
          {
            text: "Security & Compliance Built-In",
            description:
              "GDPR, HIPAA, SOC 2 ready. Enterprise-grade authentication, encryption, and security best practices from the start.",
            icon: Shield,
            gradient: "from-teal-500 to-emerald-500",
          },
        ],
      }}
      technologies={{
        title: "Built With Modern Tech",
        subtitle: "We use the same technologies as top startups and enterprises — ensuring your MVP is future-proof.",
        badge: { icon: Zap, text: "Tech Stack", color: "green" },
        items: [
          {
            name: "Frontend",
            description: "React, Next.js, Vue.js, Flutter for blazing-fast UIs",
            tools: ["React", "Next.js", "Vue", "Flutter"],
          },
          {
            name: "Backend",
            description: "Node.js, Python, Go for scalable APIs and services",
            tools: ["Node.js", "Python", "Go", "GraphQL"],
          },
          {
            name: "Cloud",
            description: "AWS, GCP, Azure for reliable, scalable infrastructure",
            tools: ["AWS", "GCP", "Azure", "Docker"],
          },
          {
            name: "AI/ML",
            description: "OpenAI, TensorFlow, custom models for intelligent features",
            tools: ["OpenAI", "TensorFlow", "LangChain", "RAG"],
          },
          {
            name: "Database",
            description: "PostgreSQL, MongoDB, Redis for performance at any scale",
            tools: ["PostgreSQL", "MongoDB", "Redis", "Firebase"],
          },
          {
            name: "DevOps",
            description: "CI/CD, monitoring, auto-scaling for zero-downtime ops",
            tools: ["GitHub Actions", "Kubernetes", "Terraform", "Datadog"],
          },
        ],
        checkColor: "text-emerald-500",
      }}
      industries={{
        badge: { icon: Building2, text: "Industries We Serve", color: "green" },
        title: "MVPs Across Every Industry",
        subtitle: "From fintech to healthcare, we've launched successful products in diverse sectors.",
        items: [
          { name: "SaaS & Enterprise", percentage: 35, projects: 35, icon: Briefcase },
          { name: "HealthTech", percentage: 20, projects: 20, icon: Stethoscope },
          { name: "EdTech", percentage: 18, projects: 18, icon: GraduationCap },
          { name: "E-Commerce & Retail", percentage: 15, projects: 15, icon: ShoppingCart },
          { name: "FinTech", percentage: 12, projects: 12, icon: BarChart3 },
        ],
        progressGradient: "from-emerald-500 to-green-500",
        progressIconColor: "text-emerald-500",
        statBoxes: [
          { label: "Industries Served", value: "15+", icon: Building2 },
          { label: "Countries Reached", value: "20+", icon: Globe },
          { label: "Avg. Project Duration", value: "2.5 Wks", icon: Clock },
          { label: "Repeat Clients", value: "85%", icon: Users },
        ],
      }}
      cta={{
        title: "Ready to Launch Your MVP?",
        description: "Get a free consultation and detailed quote within 24 hours. No commitment, NDA available.",
        buttonText: "Start Your Project Today",
        buttonOnClick: () => navigate('/contact'),
        gradient: "from-emerald-600 to-green-600",
        buttonTextColor: "text-white",
      }}
    >
      {/* Double Diamond Process */}
      <DoubleDiamondProcess />

      {/* Why Build an MVP Section */}
      <ValuePropsGrid
        badge={{ icon: Target, text: "Why Build an MVP?" }}
        title="The Smart Way to"
        titleHighlight="Launch Your Startup"
        subtitle="An MVP isn't about building less — it's about learning faster and spending smarter."
        items={whyMVP}
        accentColor="primary"
      />

      {/* What We Build Section */}
      <CapabilitiesGrid
        badge={{ icon: Layers, text: "Full-Stack Capabilities", color: "emerald" }}
        title="Everything You Need to"
        titleHighlight="Build & Scale"
        subtitle="We don't just build MVPs — we build complete tech ecosystems. From mobile apps to enterprise backends, one team handles everything."
        items={whatWeBuild}
        hoverColor="emerald"
      />

      {/* Why Build With Us Stats */}
      <WhyChooseUsSection
        title="Why Build Your Entire Stack With Us?"
        subtitle="Fragmented development teams lead to fragmented products. One team = faster delivery, lower cost, seamless integration."
        stats={[
          { value: "50%", label: "Faster than coordinating multiple vendors" },
          { value: "Zero", label: "Integration headaches between systems" },
          { value: "1 Team", label: "Single point of contact, full accountability" },
        ]}
        accentColor="green"
      />

      {/* Case Studies Section */}
      <section className="py-12 md:py-20 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <AnimateOnScroll animation="fade-up">
            <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <Rocket className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Success Stories</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 px-2">
                MVPs We've <span className="text-emerald-600 dark:text-emerald-400">Launched</span>
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground px-2">
                Real results from real startups. See how we helped founders go from idea to funded product.
              </p>
            </div>
          </AnimateOnScroll>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-5xl mx-auto">
            {caseStudies.map((study, index) => (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <article className="group p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl bg-card border border-border hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-300">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br ${study.gradient} flex items-center justify-center flex-shrink-0`}
                    >
                      <study.icon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 flex-wrap">
                        <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {study.industry}
                        </span>
                        <span className="text-[10px] sm:text-xs text-muted-foreground">•</span>
                        <span className="text-[10px] sm:text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          {study.timeline}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors leading-tight">
                        {study.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">{study.description}</p>

                  <div className="mb-3 sm:mb-4">
                    <h4 className="text-xs sm:text-sm font-medium text-foreground mb-1.5 sm:mb-2">Key Results:</h4>
                    <ul className="space-y-1 sm:space-y-1.5">
                      {study.results.map((result, i) => (
                        <li key={i} className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {study.technologies.map((tech, i) => (
                      <span
                        key={i}
                        className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full bg-muted text-muted-foreground"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </article>
              </AnimateOnScroll>
            ))}
          </div>

          <AnimateOnScroll animation="fade-up" delay={400}>
            <div className="text-center mt-8 sm:mt-10 md:mt-12">
              <Link
                to="/our-work"
                className="inline-flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-emerald-600 dark:text-emerald-400 font-medium hover:underline"
              >
                View All Our Work
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Founder Testimonials Section */}
      <section className="py-12 md:py-20 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <AnimateOnScroll animation="fade-up">
            <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Founder Testimonials</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 px-2">
                What <span className="text-emerald-600 dark:text-emerald-400">Founders Say</span> About Us
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground px-2">
                Real feedback from startup founders who trusted us with their MVP development.
              </p>
            </div>
          </AnimateOnScroll>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6 max-w-5xl mx-auto">
            {founderTestimonials.map((testimonial, index) => (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <article className="relative p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl bg-card border border-border hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-300 h-full flex flex-col">
                  <Quote className="absolute top-3 right-3 sm:top-4 sm:right-4 w-6 h-6 sm:w-8 sm:h-8 text-emerald-500/20" />

                  {/* Rating */}
                  <div className="flex gap-0.5 sm:gap-1 mb-3 sm:mb-4">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-sm sm:text-base text-foreground mb-3 sm:mb-4 flex-grow">"{testimonial.quote}"</blockquote>

                  {/* Result Badge */}
                  <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-3 sm:mb-4 w-fit">
                    <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {testimonial.result}
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-2.5 sm:gap-3 pt-3 sm:pt-4 border-t border-border">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center text-white font-semibold text-sm sm:text-lg flex-shrink-0">
                      {testimonial.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm sm:text-base text-foreground truncate">{testimonial.name}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground truncate">
                        {testimonial.role}, {testimonial.company}
                      </div>
                    </div>
                    <span className="hidden sm:inline-block ml-auto px-2 py-1 text-xs font-medium rounded-full bg-muted text-muted-foreground flex-shrink-0">
                      {testimonial.industry}
                    </span>
                  </div>
                </article>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        badge={{ icon: HelpCircle, text: "FAQ", color: "green" }}
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about building your tech product with us."
        items={mvpFaqItems}
        accentColor="green"
        contactCTAText="Still have questions? We're here to help!"
        contactCTAButtonText="Get Free Consultation"
        onContactClick={() => navigate('/contact')}
      />
    </ServicePageTemplate>
  );
};

export default MVPDevelopment;
